import dayjs from "dayjs";
import * as yup from "yup";
import {
  DATE_FORMAT,
  DOCUMENT_SITE_CORE_KEY,
  LANGUAGE_CODE,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../../helpers/constants/common";
import {
  dateWithValidation,
  greaterThanZeroValidation,
  isRequiredFieldValidation,
  lessThanValueValidation,
  specialCharactersNotAllowedValidation,
  specialCharactersNotAllowedValidationForOptionalField,
  validEmailValidation,
} from "../../../helpers/constants/commonValidationSchemaUtils";
import { mandatoryCountriesForStateRegionProvince } from "../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import {
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
  MAX_LENGTH_100,
  MAX_LENGTH_180,
  MIN_LENGTH_3,
  REGEX_ABN_NUMBER,
  REGEX_ACCEPTED_OPPORTUNITY_ID_SPECIAL_CHARS_AND_SPACES,
  REGEX_BIN_NUMBER,
  REGEX_DUTCH_VAT_NUMBER,
  REGEX_INDIAN_CIN,
  REGEX_NZBN_NUMBER,
  REGEX_QUALIFIED_INVOICE_NUMBER,
  REGEX_SIRET_NUMBER,
  REGEX_SPANISH_VAT_NUMBER,
  REGEX_SPANISH_VAT_NUMBER_2,
  REGEX_SRI_LANKA_TIN,
} from "../../../helpers/constants/validations";
import {
  getValueOrBlank,
  isActivityBasedBilling,
  isBillingTypeMilestoneOrSchedule,
  isEmpty,
  isNotEmpty,
  isScheduledBasedBilling,
} from "../../../helpers/utils";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import {
  billingAndPurchaseOrderKeys,
  createClientSiteCoreKeys,
  uploadDocumentSiteCoreKeys,
} from "../siteCore/SiteCoreKeys";
import {
  AddContactFormKey,
  AddPurchaseOrderFormKey,
  ArchiveContractDocumentFormKey,
  CancelContractDocumentFormKey,
  CreateClientFormKey,
  InheritDocumentsSearchFormKey,
  POManageTransactionsFormKey,
  ProgramTemplateFormKey,
  ReopenPurchaseOrderFormKey,
  ResourcesFormKey,
  STATUS_YES,
  UploadDocumentFormKey,
} from "./Models";

const specialExtCharsValueValidation = (value: string) =>
  !value || INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(value);

const isValidOpportunityId = (value: string) =>
  !value || !REGEX_ACCEPTED_OPPORTUNITY_ID_SPECIAL_CHARS_AND_SPACES.test(value);

// Helper: Check if English company name is required based on country and language
const isEnglishCompanyNameRequired = (
  billingCountry: string,
  addressCountry: string,
  preferredLanguage?: string,
): boolean => {
  const asianCountries = [
    SITE_CORE_COUNTRIES_VALUE.JAPAN,
    SITE_CORE_COUNTRIES_VALUE.KOREA_DEMOCRATIC,
    SITE_CORE_COUNTRIES_VALUE.KOREA_REPUBLIC,
  ];

  return (
    asianCountries.includes(addressCountry) ||
    asianCountries.includes(billingCountry) ||
    preferredLanguage === LANGUAGE_CODE.JA_JP
  );
};

export const CreateClientValidationSchema = (
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
  fetchLabel: (parentKey: string, key: string) => string,
  clientCode?: string,
  preferredLanguage?: string,
) =>
  yup.object().shape(
    {
      [CreateClientFormKey.companyName]: specialCharactersNotAllowedValidation(
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
      ),
      [CreateClientFormKey.businessIdentificationNumber]: yup.string(),
      [CreateClientFormKey.englishCompanyName]: yup
        .string()
        .when(
          [
            CreateClientFormKey.billingCountry,
            CreateClientFormKey.addressCountry,
          ],
          {
            is: (billingCountry: string, addressCountry: string) => {
              return isEnglishCompanyNameRequired(
                billingCountry,
                addressCountry,
                preferredLanguage,
              );
            },
            then: (schema) =>
              schema.required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
          },
        )
        .test(
          "Special Character Validation",
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) => specialExtCharsValueValidation(getValueOrBlank(value)),
        ),

      [CreateClientFormKey.clientType]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.industry]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.localClientRelationshipOwnerId]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.addressCountry]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.addressCountryName]: yup.string(),
      [CreateClientFormKey.billingCountry]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.billingCountryName]: yup.string(),
      [CreateClientFormKey.taxVat]: yup.string().optional(),
      [CreateClientFormKey.dedicatedProjectManager]: yup.string(),
      [CreateClientFormKey.originDate]: yup.string(),
      [CreateClientFormKey.address1]: specialCharactersNotAllowedValidation(
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
      ),
      [CreateClientFormKey.address2]:
        specialCharactersNotAllowedValidationForOptionalField(
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
        ),
      [CreateClientFormKey.address3]:
        specialCharactersNotAllowedValidationForOptionalField(
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
        ),
      [CreateClientFormKey.country]: yup.string(),
      [CreateClientFormKey.countryName]: yup.string(),
      [CreateClientFormKey.city]: specialCharactersNotAllowedValidation(
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
      ),
      [CreateClientFormKey.stateProvince]: yup
        .string()
        .when([CreateClientFormKey.addressCountry], {
          is: (addressCountry: string) => {
            return mandatoryCountriesForStateRegionProvince.includes(
              addressCountry,
            );
          },
          then: (schema) =>
            schema.required(
              fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
            ),
        })
        .test(
          EXTENDED_SPECIAL_CHARS,
          fetchValidationMessage(
            commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
          ),
          (value) => specialExtCharsValueValidation(getValueOrBlank(value)),
        ),
      [CreateClientFormKey.postalCode]: yup.string().optional(),
      [CreateClientFormKey.siretNumber]: yup
        .string()
        .optional()
        .when(
          [CreateClientFormKey.siretNumber, CreateClientFormKey.addressCountry],
          {
            is: (siretNumber: string, addressCountry: string) => {
              const isAddressFrance =
                addressCountry?.toLowerCase() ===
                SITE_CORE_COUNTRIES_VALUE.FRANCE.toLowerCase();
              return !!siretNumber || isAddressFrance;
            },

            then: (schema: yup.StringSchema) =>
              schema
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                )
                .matches(REGEX_SIRET_NUMBER, {
                  message: fetchValidationMessage(
                    commonSiteCoreKeys.invalidValueValidator,
                    {
                      LABELNAME: fetchLabel(
                        createClientSiteCoreKeys.createClientProfileTitle,
                        createClientSiteCoreKeys.siretNumber,
                      ),
                    },
                  ),
                  excludeEmptyString: true,
                }),
          },
        ),
      [CreateClientFormKey.btwNummer]: yup
        .string()
        .optional()
        .when(
          [
            CreateClientFormKey.billingCountry,
            CreateClientFormKey.addressCountry,
          ],
          {
            is: (billingCountry: string, addressCountry: string) => {
              const isBillingNetherlands =
                billingCountry?.toLowerCase() ===
                SITE_CORE_COUNTRIES_VALUE.NETHERLANDS.toLowerCase();
              const isAddressNetherlands =
                addressCountry?.toLowerCase() ===
                SITE_CORE_COUNTRIES_VALUE.NETHERLANDS.toLowerCase();

              // Only validate if both countries are the same (either both Netherlands or both not)
              return isAddressNetherlands === isBillingNetherlands;
            },

            then: (schema: yup.StringSchema) => {
              return schema.when([CreateClientFormKey.btwNummer], {
                is: (btwNummer: string) => {
                  return !!btwNummer;
                },

                then: (schema: yup.StringSchema) => {
                  return schema.matches(REGEX_DUTCH_VAT_NUMBER, {
                    message: fetchValidationMessage(
                      commonSiteCoreKeys.invalidValueValidator,
                      {
                        LABELNAME: fetchLabel(
                          createClientSiteCoreKeys.createClientProfileTitle,
                          createClientSiteCoreKeys.btwNummer,
                        ),
                      },
                    ),
                    excludeEmptyString: true,
                  });
                },
              });
            },
          },
        ),
      [CreateClientFormKey.nif]: yup
        .string()
        .optional()
        .when(
          [
            CreateClientFormKey.billingCountry,
            CreateClientFormKey.addressCountry,
          ],
          {
            is: (billingCountry: string, addressCountry: string) => {
              const isAddressSpain =
                addressCountry?.toLowerCase() ===
                SITE_CORE_COUNTRIES_VALUE.SPAIN.toLowerCase();
              const isBillingSpain =
                billingCountry?.toLowerCase() ===
                SITE_CORE_COUNTRIES_VALUE.SPAIN.toLowerCase();

              // Return true if both are Spain or both are not Spain, otherwise false
              return isAddressSpain === isBillingSpain;
            },

            then: (schema: yup.StringSchema) => {
              return schema.when([CreateClientFormKey.nif], {
                is: (nif: string) => {
                  return !!nif;
                },

                then: (schema: yup.StringSchema) => {
                  return schema.matches(REGEX_SPANISH_VAT_NUMBER, {
                    message: fetchValidationMessage(
                      commonSiteCoreKeys.invalidValueValidator,
                      {
                        LABELNAME: fetchLabel(
                          createClientSiteCoreKeys.createClientProfileTitle,
                          createClientSiteCoreKeys.nifNumber,
                        ),
                      },
                    ),
                    excludeEmptyString: true,
                  });
                },
              });
            },
            otherwise: (schema) => {
              return schema.when([CreateClientFormKey.nif], {
                is: (nif: string) => {
                  return !!nif;
                },

                then: (schema: yup.StringSchema) => {
                  return schema.matches(REGEX_SPANISH_VAT_NUMBER_2, {
                    message: fetchValidationMessage(
                      commonSiteCoreKeys.invalidValueValidator,
                      {
                        LABELNAME: fetchLabel(
                          createClientSiteCoreKeys.createClientProfileTitle,
                          createClientSiteCoreKeys.nifNumber,
                        ),
                      },
                    ),
                    excludeEmptyString: true,
                  });
                },
              });
            },
          },
        ),
      [CreateClientFormKey.abnNumber]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.abnNumber], {
          is: (abnNumber: string) => {
            return !!abnNumber;
          },
          then: (schema) =>
            schema.matches(REGEX_ABN_NUMBER, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.abnNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),

      [CreateClientFormKey.nzbn]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.nzbn], {
          is: (nzbn: string) => {
            return !!nzbn;
          },
          then: (schema) =>
            schema.matches(REGEX_NZBN_NUMBER, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.nzbnNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),
      [CreateClientFormKey.qualifiedInvoiceNumber]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.qualifiedInvoiceNumber], {
          is: (qualifiedInvoiceNumber: string) => {
            return !!qualifiedInvoiceNumber;
          },
          then: (schema) =>
            schema.matches(REGEX_QUALIFIED_INVOICE_NUMBER, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.qualifiedInvoiceNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),
      [CreateClientFormKey.cin]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.cin], {
          is: (cin: string) => {
            return !!cin;
          },
          then: (schema) =>
            schema.matches(REGEX_INDIAN_CIN, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.cinNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),
      [CreateClientFormKey.bin]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.bin], {
          is: (bin: string) => {
            return !!bin;
          },
          then: (schema) =>
            schema.matches(REGEX_BIN_NUMBER, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.binNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),

      [CreateClientFormKey.tin]: yup
        .string()
        .optional()
        .when([CreateClientFormKey.tin], {
          is: (tin: string) => {
            return !!tin;
          },
          then: (schema) =>
            schema.matches(REGEX_SRI_LANKA_TIN, {
              message: fetchValidationMessage(
                commonSiteCoreKeys.invalidValueValidator,
                {
                  LABELNAME: fetchLabel(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.tinNumber,
                  ),
                },
              ),
              excludeEmptyString: true,
            }),
        }),

      [CreateClientFormKey.consolidatedInvoicing]: yup.boolean(),
      [CreateClientFormKey.activeCompany]: yup.boolean(),
      [CreateClientFormKey.rebateClient]: yup.boolean(),
      [CreateClientFormKey.earlyPayDiscount]: yup.boolean(),
      [CreateClientFormKey.earlyPayDays]: yup
        .string()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .when([CreateClientFormKey.earlyPayDiscount], {
          is: (earlyPayDiscount: boolean) => !!earlyPayDiscount,
          then: () =>
            yup
              .string()
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
          otherwise: () => yup.string().optional().nullable(),
        }),
      [CreateClientFormKey.earlyPayPercentage]: yup
        .string()
        .transform((value, originalValue) =>
          originalValue === "" ? null : value,
        )
        .when([CreateClientFormKey.earlyPayDiscount], {
          is: (earlyPayDiscount: boolean) => !!earlyPayDiscount,
          then: () =>
            yup
              .string()
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              )
              .test(
                "is-less-than-10",
                fetchLabel(
                  billingAndPurchaseOrderKeys.billingDetailsAndPOTitle,
                  billingAndPurchaseOrderKeys.earlyPayPercentageErrorMessage,
                ),
                (value) => {
                  if (isEmpty(value)) {
                    return true;
                  }
                  const numberValue = Number(value);
                  return !isNaN(numberValue) && numberValue < 10;
                },
              ),

          otherwise: () => yup.string().optional().nullable(),
        }),
      [CreateClientFormKey.invoiceLanguage]: isRequiredFieldValidation(
        isNotEmpty(clientCode),
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
      ),
      [CreateClientFormKey.notes]: yup.string().nullable(),
      [CreateClientFormKey.configurations]: clientCode
        ? yup.array().of(
            yup.object().shape({
              [CreateClientFormKey.billingConfigurationName]:
                specialCharactersNotAllowedValidation(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                  fetchValidationMessage(
                    commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
                  ),
                ),
              [CreateClientFormKey.lineOfBusinessId]: yup
                .string()
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                ),
              [CreateClientFormKey.opportunityId]: yup
                .string()
                .test(
                  "is-valid-opportunity-id",
                  fetchValidationMessage(
                    commonSiteCoreKeys.specialCharNotAllowed,
                  ),
                  (value) => isValidOpportunityId(getValueOrBlank(value)),
                ),
              [CreateClientFormKey.opportunityName]: yup.string(),
              [CreateClientFormKey.isOpportunityIdValidated]: yup.boolean(),
              [CreateClientFormKey.paymentTermId]: yup
                .string()
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                ),
              [CreateClientFormKey.billingContactId]: yup
                .string()
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                ),
              [CreateClientFormKey.hrContactId]: yup
                .string()
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                ),
              [CreateClientFormKey.outreachEligibilityId]: yup
                .string()
                .required(
                  fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
                ),
              [CreateClientFormKey.costCentre]: yup.string(),
              [CreateClientFormKey.businessUnit]: yup.string(),
              [CreateClientFormKey.poNumberId]: yup.string(),
              [CreateClientFormKey.candidateLocation]: yup.string(),
              [CreateClientFormKey.site]: yup.string(),
              [CreateClientFormKey.projectManagerId]: yup.string(),
              [CreateClientFormKey.projectCoordinatorId]: yup.string(),
              [CreateClientFormKey.additionalProjectResourceId]: yup.string(),
              [CreateClientFormKey.clientInformation1]: yup.string(),
              [CreateClientFormKey.clientInformation2]: yup.string(),
              [CreateClientFormKey.clientInformation3]: yup.string(),
              [CreateClientFormKey.hasEmployeeBand]: yup.boolean(),
              [CreateClientFormKey.hasEmployeeId]: yup.boolean(),

              [CreateClientFormKey.programTemplateConfigurations]: yup
                .array()
                .of(
                  yup.object().shape({
                    [CreateClientFormKey.specialBillingInstructions]:
                      yup.string(),
                    [CreateClientFormKey.programTemplateId]: yup
                      .string()
                      .required(
                        fetchValidationMessage(
                          commonSiteCoreKeys.requiredValidation,
                        ),
                      ),
                    [CreateClientFormKey.alternateDisplayName]:
                      specialCharactersNotAllowedValidationForOptionalField(
                        fetchValidationMessage(
                          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
                        ),
                      ),
                    [CreateClientFormKey.billingTypeId]: yup
                      .string()
                      .required(
                        fetchValidationMessage(
                          commonSiteCoreKeys.requiredValidation,
                        ),
                      ),
                    [CreateClientFormKey.billingTriggerId]: yup
                      .string()
                      .optional()
                      .when([CreateClientFormKey.billingTypeId], {
                        is: (billingTypeId: string) =>
                          isActivityBasedBilling(billingTypeId),
                        then: () =>
                          yup
                            .string()
                            .required(
                              fetchValidationMessage(
                                commonSiteCoreKeys.requiredValidation,
                              ),
                            ),
                      }),
                    [CreateClientFormKey.currencyId]: yup.string(),
                    [CreateClientFormKey.fee]: yup
                      .string()
                      .required(
                        fetchValidationMessage(
                          commonSiteCoreKeys.requiredValidation,
                        ),
                      ),
                    [CreateClientFormKey.feeWithFormat]: yup
                      .string()
                      .required(
                        fetchValidationMessage(
                          commonSiteCoreKeys.requiredValidation,
                        ),
                      ),

                    [CreateClientFormKey.schedulePeriod]: yup
                      .string()
                      .optional()
                      .when([CreateClientFormKey.billingTypeId], {
                        is: (billingTypeId: string) =>
                          isScheduledBasedBilling(billingTypeId),
                        then: () =>
                          yup
                            .string()
                            .required(
                              fetchValidationMessage(
                                commonSiteCoreKeys.requiredValidation,
                              ),
                            ),
                      }),
                    [CreateClientFormKey.feePaymentType]: yup
                      .string()
                      .optional()
                      .when([CreateClientFormKey.billingTypeId], {
                        is: (billingTypeId: string) =>
                          isBillingTypeMilestoneOrSchedule(billingTypeId),
                        then: () =>
                          yup
                            .string()
                            .required(
                              fetchValidationMessage(
                                commonSiteCoreKeys.requiredValidation,
                              ),
                            ),
                      }),
                  }),
                ),
            }),
          )
        : yup.array().default([]),
      [CreateClientFormKey.salesOfficeId]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [CreateClientFormKey.salesOfficeName]: yup
        .string()
        .notRequired()
        .default(""),
    },
    [
      [CreateClientFormKey.abnNumber, CreateClientFormKey.abnNumber],
      [CreateClientFormKey.nzbn, CreateClientFormKey.nzbn],
      [
        CreateClientFormKey.qualifiedInvoiceNumber,
        CreateClientFormKey.qualifiedInvoiceNumber,
      ],
      [CreateClientFormKey.bin, CreateClientFormKey.bin],
      [CreateClientFormKey.cin, CreateClientFormKey.cin],
      [CreateClientFormKey.tin, CreateClientFormKey.tin],
      [CreateClientFormKey.siretNumber, CreateClientFormKey.siretNumber],
    ],
  );

export const AddNewContactValidationSchema = (
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
  fetchLabel: (parentKey: string, key: string) => string,
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape(
    {
      [AddContactFormKey.prefix]: yup.string(),
      [AddContactFormKey.firstName]: specialCharactersNotAllowedValidation(
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
      ),
      [AddContactFormKey.lastName]: specialCharactersNotAllowedValidation(
        fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        fetchValidationMessage(
          commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
        ),
      ),
      [AddContactFormKey.jobTitle]: yup
        .string()
        .optional()
        .when([AddContactFormKey.jobTitle], {
          is: (jobTitle: string) => {
            return !!jobTitle;
          },
          then: (schema) =>
            schema
              .test(
                EXTENDED_SPECIAL_CHARS,
                fetchValidationMessage(
                  commonSiteCoreKeys.fieldContainsInvalidCharactersErrorMessage,
                ),
                (value) =>
                  specialExtCharsValueValidation(getValueOrBlank(value)),
              )
              .min(
                MIN_LENGTH_3,
                fetchLabel(
                  createClientSiteCoreKeys.addContactTitle,
                  createClientSiteCoreKeys.jobTitleValidationMessage,
                ),
              ),
        }),
      [AddContactFormKey.workEmail]: validEmailValidation(
        fetchValidationMessage(commonSiteCoreKeys.invalidEmail),
      ).required(fetchValidationMessage(commonSiteCoreKeys.requiredValidation)),
      [AddContactFormKey.mobileNumber]: yup.string(),
      [AddContactFormKey.telephoneNumber]: yup.string(),
      [AddContactFormKey.contactType]: yup.array().of(yup.string()),
      [AddContactFormKey.isSurveyContact]: yup.boolean(),
      [AddContactFormKey.surveyLanguage]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [AddContactFormKey.telephoneCountryCode]: yup.string(),
      [AddContactFormKey.mobileCountryCode]: yup.string(),
      [AddContactFormKey.status]: yup.string(),
      [AddContactFormKey.telephoneISOCode]: yup.string(),
      [AddContactFormKey.mobileISOCode]: yup.string(),
    },
    [[AddContactFormKey.jobTitle, AddContactFormKey.jobTitle]],
  );

export const CreateResourceValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [ResourcesFormKey.resources]: yup.array().of(
      yup.object().shape({
        [ResourcesFormKey.resourceName]: specialCharactersNotAllowedValidation(
          commonValidationMessages.requiredValidation,
          commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        ),
        [ResourcesFormKey.resourceId]: yup
          .string()
          .required(commonValidationMessages.requiredValidation),
        [ResourcesFormKey.resourceTypeId]: yup
          .string()
          .required(commonValidationMessages.requiredValidation),
        [ResourcesFormKey.languageId]: yup
          .string()
          .required(commonValidationMessages.requiredValidation),
      }),
    ),
  });

export const CreateProgramTemplateValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape(
    {
      [ProgramTemplateFormKey.groupName]: specialCharactersNotAllowedValidation(
        commonValidationMessages.requiredValidation,
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ),
      [ProgramTemplateFormKey.programTemplates]: yup.array().of(
        yup.object().shape({
          [ProgramTemplateFormKey.programTemplateId]: yup
            .string()
            .required(commonValidationMessages.requiredValidation),
          [ProgramTemplateFormKey.countries]: yup
            .array()
            .min(1, commonValidationMessages.requiredValidation)
            .required(commonValidationMessages.requiredValidation),
        }),
      ),
    },
    [[ProgramTemplateFormKey.showCountry, ProgramTemplateFormKey.showCountry]],
  );

export const UploadDocumentModalValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userCountryDateFormat: string,
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
  fetchLabel: (parentKey: string, key: string) => string,
  minCountriesCount: number,
  minCountriesErrorMessage?: string,
) =>
  yup.object().shape(
    {
      [UploadDocumentFormKey.contractId]: yup.string(),
      [UploadDocumentFormKey.opportunityId]: yup
        .string()
        .test(
          "is-valid-opportunity-id",
          fetchValidationMessage(commonSiteCoreKeys.specialCharNotAllowed),
          (value) => isValidOpportunityId(getValueOrBlank(value)),
        ),
      [UploadDocumentFormKey.opportunityName]: yup.string(),
      [UploadDocumentFormKey.isOpportunityIdValidated]: yup.boolean(),
      [UploadDocumentFormKey.ownershipCountryId]: yup.string(),
      [UploadDocumentFormKey.classificationId]: yup
        .string()
        .when([UploadDocumentFormKey.documentType], {
          is: (documentType: string) =>
            documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract,
          then: () =>
            yup
              .string()
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
        }),
      [UploadDocumentFormKey.contractGeoScopeId]: yup
        .string()
        .when([UploadDocumentFormKey.documentType], {
          is: (documentType: string) =>
            documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract,
          then: () =>
            yup
              .string()
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
        }),
      [UploadDocumentFormKey.contractProductLines]: yup
        .array()
        .of(yup.string())
        .when([UploadDocumentFormKey.documentType], {
          is: (documentType: string) =>
            documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract,
          then: () =>
            yup
              .array()
              .of(yup.string())
              .min(
                1,
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              )
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
        }),
      [UploadDocumentFormKey.customDeliverablesId]: yup.string(),
      [UploadDocumentFormKey.documentType]: yup
        .string()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),
      [UploadDocumentFormKey.contractDuration]: lessThanValueValidation(
        MAX_LENGTH_180,
        fetchLabel(
          uploadDocumentSiteCoreKeys.uploadDocumentTitle,
          uploadDocumentSiteCoreKeys.maximumOf180MonthsAllowed,
        ),
      ).concat(
        greaterThanZeroValidation(
          fetchValidationMessage(
            commonSiteCoreKeys.greaterThanZeroFieldValidator,
          ),
        ),
      ),
      [UploadDocumentFormKey.engagementTypeId]: yup.string(),
      [UploadDocumentFormKey.exclusivityId]: yup.string(),
      [UploadDocumentFormKey.paymentTermId]: yup
        .string()
        .when([UploadDocumentFormKey.documentType], {
          is: (documentType: string) =>
            documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract,
          then: () =>
            yup
              .string()
              .required(
                fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
              ),
        }),

      [UploadDocumentFormKey.serviceLevelAgreementsId]: yup.string(),
      [UploadDocumentFormKey.sowAutoRenewalClauseId]: yup.string(),
      [UploadDocumentFormKey.sowExpirationDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
      ).when([UploadDocumentFormKey.sowContractEffectiveDate], {
        is: (sowContractEffectiveDate: string) => {
          return !!sowContractEffectiveDate;
        },
        then: (schema) => {
          return schema.test(
            "is-before",
            fetchLabel(
              uploadDocumentSiteCoreKeys.uploadDocumentTitle,
              uploadDocumentSiteCoreKeys.expirationDateMustBeGreaterThanEffectiveDate,
            ),
            (_, value) => {
              if (!value.parent?.sowExpiryDateWithoutFormatted) {
                return true;
              }
              const effectiveDateWithoutFormatted = dayjs(
                value.parent?.sowEffectiveDateWithoutFormatted?.format(
                  DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                ),
              );
              const expiryDateWithoutFormatted = dayjs(
                value.parent?.sowExpiryDateWithoutFormatted?.format(
                  DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                ),
              );
              return effectiveDateWithoutFormatted?.isBefore(
                expiryDateWithoutFormatted,
              );
            },
          );
        },
      }),
      [UploadDocumentFormKey.sowStatusId]: yup.string(),
      [UploadDocumentFormKey.uploadDate]: yup.string(),
      [UploadDocumentFormKey.sowAutoRenewalTerms]: lessThanValueValidation(
        MAX_LENGTH_100,
        fetchLabel(
          uploadDocumentSiteCoreKeys.uploadDocumentTitle,
          uploadDocumentSiteCoreKeys.maximumOf100MonthsAllowed,
        ),
      )
        .concat(
          greaterThanZeroValidation(
            fetchValidationMessage(
              commonSiteCoreKeys.greaterThanZeroFieldValidator,
            ),
          ),
        )
        .when([UploadDocumentFormKey.sowAutoRenewalClauseId], {
          is: (sowAutoRenewalClauseId: string) => {
            return sowAutoRenewalClauseId === STATUS_YES;
          },
          then: (schema) => {
            return schema.required(
              fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
            );
          },
        }),
      [UploadDocumentFormKey.sowContractEffectiveDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
      ).when([UploadDocumentFormKey.documentType], {
        is: (documentType: string) => {
          return documentType === DOCUMENT_SITE_CORE_KEY.documentTypeContract;
        },
        then: (schema) => {
          return schema
            .required(
              fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
            )
            .when([UploadDocumentFormKey.sowExpirationDate], {
              is: (expiryDate: string) => {
                return !!expiryDate;
              },
              then: (schema) => {
                return schema.test(
                  "is-after",
                  fetchLabel(
                    uploadDocumentSiteCoreKeys.uploadDocumentTitle,
                    uploadDocumentSiteCoreKeys.effectiveDateMustBeLesserThanExpirationDate,
                  ),
                  (_, value) => {
                    if (!value.parent?.sowEffectiveDateWithoutFormatted) {
                      return true;
                    }
                    const effectiveDateWithoutFormatted = dayjs(
                      value.parent?.sowEffectiveDateWithoutFormatted?.format(
                        DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                      ),
                    );
                    const expiryDateWithoutFormatted = dayjs(
                      value.parent?.sowExpiryDateWithoutFormatted?.format(
                        DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                      ),
                    );
                    return expiryDateWithoutFormatted?.isAfter(
                      effectiveDateWithoutFormatted,
                    );
                  },
                );
              },
            });
        },
      }),
      [UploadDocumentFormKey.sowExpiryDateWithoutFormatted]: yup
        .object()
        .nullable(),
      [UploadDocumentFormKey.sowEffectiveDateWithoutFormatted]: yup
        .object()
        .nullable(),
      [UploadDocumentFormKey.effectiveDate]: dateWithValidation(
        true,
        commonValidationMessages,
        false,
        false,
        userCountryDateFormat,
      ),
      [UploadDocumentFormKey.file]: yup
        .mixed()
        .required(
          fetchValidationMessage(commonSiteCoreKeys.requiredValidation),
        ),

      [UploadDocumentFormKey.notes]: yup.string(),
      [UploadDocumentFormKey.contractGeoScopeSingleCountry]:
        minCountriesCount === 1
          ? yup.string().when([UploadDocumentFormKey.contractGeoScopeId], {
              is: (contractGeoScopeId: boolean) => !!contractGeoScopeId,
              then: () =>
                yup
                  .string()
                  .min(minCountriesCount, minCountriesErrorMessage)
                  .required(
                    fetchValidationMessage(
                      commonSiteCoreKeys.requiredValidation,
                    ),
                  ),
            })
          : yup.string().optional(),
      [UploadDocumentFormKey.contractGeoScopeCountries]:
        minCountriesCount === 2
          ? yup
              .array()
              .of(yup.string())
              .when([UploadDocumentFormKey.contractGeoScopeId], {
                is: (contractGeoScopeId: boolean) => !!contractGeoScopeId,
                then: () =>
                  yup
                    .array()
                    .of(yup.string())
                    .min(minCountriesCount, minCountriesErrorMessage)
                    .required(
                      fetchValidationMessage(
                        commonSiteCoreKeys.requiredValidation,
                      ),
                    ),
              })
          : yup.array().of(yup.string()).optional(),
      [UploadDocumentFormKey.statusId]: yup.string(),
    },
    [
      [UploadDocumentFormKey.documentType, UploadDocumentFormKey.documentType],
      [
        UploadDocumentFormKey.sowExpirationDate,
        UploadDocumentFormKey.sowContractEffectiveDate,
      ],
    ],
  );

export const InheritDocumentsValidationSchema = (
  validationMessages: KeyValueMap,
) =>
  yup.object().shape(
    {
      [InheritDocumentsSearchFormKey.documentName]: yup
        .string()
        .optional()
        .when([InheritDocumentsSearchFormKey.documentName], {
          is: (documentName: string) => {
            return !!documentName;
          },
          then: (schema) =>
            schema.min(MIN_LENGTH_3, validationMessages.min3CharsValidation),
        }),
      [InheritDocumentsSearchFormKey.documentType]: yup.string(),
      [InheritDocumentsSearchFormKey.contractId]: yup.string(),
    },
    [
      [
        InheritDocumentsSearchFormKey.documentName,
        InheritDocumentsSearchFormKey.documentName,
      ],
    ],
  );

export const CancelContractDocumentModalValidationSchema = (
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
) =>
  yup.object().shape({
    [CancelContractDocumentFormKey.cancellationReason]: yup
      .string()
      .required(fetchValidationMessage(commonSiteCoreKeys.requiredValidation)),
    [CancelContractDocumentFormKey.comment]: yup.string(),
  });

export const ArchiveContractDocumentModalValidationSchema = (
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
) =>
  yup.object().shape({
    [ArchiveContractDocumentFormKey.archivalReason]: yup
      .string()
      .required(fetchValidationMessage(commonSiteCoreKeys.requiredValidation)),
    [ArchiveContractDocumentFormKey.archivalComments]: yup.string(),
  });

export const AddPurchaseOrderValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userCountryDateFormat?: string,
) =>
  yup.object().shape(
    {
      [AddPurchaseOrderFormKey.poNumber]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [AddPurchaseOrderFormKey.effectiveDate]: yup
        .string()
        .required(commonValidationMessages.requiredValidation)
        .default("")
        .when([AddPurchaseOrderFormKey.expiryDate], {
          is: (expiryDate: string) => {
            return !!expiryDate;
          },
          then: (schema) => {
            return schema.test(
              "is-before",
              commonValidationMessages.effectiveDateMustBeLesserThanExpirationDate,
              (_, value) => {
                const effectiveDateWithoutFormatted = dayjs(
                  value.parent?.effectiveDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );
                const expiryDateWithoutFormatted = dayjs(
                  value.parent?.expiryDateWithoutFormatted?.format(
                    DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                  ),
                );

                return effectiveDateWithoutFormatted?.isBefore(
                  expiryDateWithoutFormatted,
                );
              },
            );
          },
        }),
      [AddPurchaseOrderFormKey.initialAmount]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [AddPurchaseOrderFormKey.currencyTypeId]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
      [AddPurchaseOrderFormKey.statusId]: yup.string(),
      [AddPurchaseOrderFormKey.expiryDate]: dateWithValidation(
        true,
        commonValidationMessages,
        true,
        false,
        userCountryDateFormat,
      ).when([AddPurchaseOrderFormKey.effectiveDate], {
        is: (effectiveDate: string) => {
          return !!effectiveDate;
        },
        then: (schema) => {
          return schema.test(
            "is-after",
            commonValidationMessages.expirationDateMustBeGreaterThanEffectiveDate,
            (_, value) => {
              if (!value.parent?.expiryDateWithoutFormatted) {
                return true;
              }
              const effectiveDateWithoutFormatted = dayjs(
                value.parent?.effectiveDateWithoutFormatted?.format(
                  DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                ),
              );
              const expiryDateWithoutFormatted = dayjs(
                value.parent?.expiryDateWithoutFormatted?.format(
                  DATE_FORMAT.YYYY_MM_DD_HYPHEN,
                ),
              );
              return expiryDateWithoutFormatted?.isAfter(
                effectiveDateWithoutFormatted,
              );
            },
          );
        },
      }),

      [AddPurchaseOrderFormKey.effectiveDateWithoutFormatted]: yup
        .object()
        .nullable(),
      [AddPurchaseOrderFormKey.expiryDateWithoutFormatted]: yup
        .object()
        .nullable(),
      [AddPurchaseOrderFormKey.notes]: yup.string(),
      [AddPurchaseOrderFormKey.initialAmountFormatted]:
        greaterThanZeroValidation(
          commonValidationMessages.greaterThanZeroFieldValidator,
        ).required(commonValidationMessages.requiredValidation),
    },
    [
      [
        AddPurchaseOrderFormKey.effectiveDate,
        AddPurchaseOrderFormKey.expiryDate,
      ],
    ],
  );

export const ReOpenPurchaseOrderValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [ReopenPurchaseOrderFormKey.reOpenReason]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [ReopenPurchaseOrderFormKey.expiryDate]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
  });

export const ManageTransactionValidationSchema = (
  fetchValidationMessage: (key: string, replacements?: KeyValueMap) => string,
) =>
  yup.object().shape({
    [POManageTransactionsFormKey.transactionType]: yup.number().required(),
    [POManageTransactionsFormKey.transactionAmount]: yup
      .string()
      .required(fetchValidationMessage(commonSiteCoreKeys.requiredValidation)),
  });
